// LeadDetailPage — карточка лида (V12 U-34-E): поля заявки + статус + источник,
// кнопки «Превратить в сделку» (LeadConvertDialog) и «Редактировать»
// (диалог полей + статус → PATCH /api/leads/{id} с expectedUpdatedAt).
// KISS: без вкладок/платежей — они в волне R70.

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EditIcon from '@mui/icons-material/Edit';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { leadsApi, type Lead, type LeadStatus } from '@/api/leads';
import type { Deal } from '@/api/deals';
import { PaymentBlock } from '@/components/PaymentBlock';
import { LeadConvertDialog } from './components/LeadConvertDialog';
import { LeadStatusChip } from './components/LeadStatusChip';
import { extractApiErrorMessage } from '@/lib/apiError';
import { formatDate, formatLeadSource, formatLeadStatus } from '@/lib/format';
import { t } from '@/i18n';

/** Порядок статусов в селекте редактора (канон StatusChip). */
const LEAD_STATUS_ORDER: LeadStatus[] = ['NEW', 'IN_PROGRESS', 'CONVERTED', 'DISCARDED'];

/** Значения редактора лида (пустая строка = null на проводе). */
interface LeadEditValues {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  licenseNumber: string;
  siteName: string;
  ipAddress: string;
  notes: string;
  status: LeadStatus;
}

/** Строит значение редактора из лида. */
function toEditValues(lead: Lead): LeadEditValues {
  return {
    title: lead.title,
    firstName: lead.firstName ?? '',
    lastName: lead.lastName ?? '',
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    companyName: lead.companyName ?? '',
    licenseNumber: lead.licenseNumber ?? '',
    siteName: lead.siteName ?? '',
    ipAddress: lead.ipAddress ?? '',
    notes: lead.notes ?? '',
    status: lead.status,
  };
}

/** Поле карточки: подпись + значение (канон ContactDetailPage). */
function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

/** Детальная страница лида. */
export function LeadDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [editValues, setEditValues] = useState<LeadEditValues | null>(null);

  const leadQuery = useQuery({
    queryKey: ['lead', id],
    queryFn: () => leadsApi.get(id ?? ''),
    enabled: Boolean(id),
  });

  const editMutation = useMutation({
    mutationFn: () => {
      if (!leadQuery.data || !editValues) throw new Error('Данные лида не загружены');
      return leadsApi.update(id ?? '', {
        title: editValues.title,
        firstName: editValues.firstName || null,
        lastName: editValues.lastName || null,
        email: editValues.email || null,
        phone: editValues.phone || null,
        companyName: editValues.companyName || null,
        licenseNumber: editValues.licenseNumber || null,
        siteName: editValues.siteName || null,
        ipAddress: editValues.ipAddress || null,
        notes: editValues.notes || null,
        status: editValues.status,
        expectedUpdatedAt: leadQuery.data.updatedAt,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lead', id] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      setEditOpen(false);
    },
    onError: () => {
      // B2-канон: 409/ошибка → refetch — свежий version, пользователь не в тупике повторных 409.
      void queryClient.invalidateQueries({ queryKey: ['lead', id] });
    },
  });

  if (leadQuery.isLoading) return <LoadingSpinner label="Загрузка лида…" />;
  if (leadQuery.isError || !leadQuery.data) {
    return (
      <EmptyState
        title={t('leads.detail.notFound')}
        description={leadQuery.error instanceof Error ? leadQuery.error.message : 'Неизвестная ошибка'}
        actionLabel={t('common.back')}
        onAction={() => navigate('/crm/leads')}
      />
    );
  }
  const lead = leadQuery.data;

  const openEdit = (): void => {
    setEditValues(toEditValues(lead));
    setEditOpen(true);
  };

  /** Успех конверсии: инвалидация списка + навигация к сделке (приёмочный критерий №4). */
  const handleConverted = (deal: Deal): void => {
    void queryClient.invalidateQueries({ queryKey: ['leads'] });
    navigate(`/crm/deals/${deal.id}`);
  };

  return (
    <Box>
      <PageHeader
        title={lead.title}
        subtitle={formatLeadStatus(lead.status, t)}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={openEdit}>
              {t('common.edit')}
            </Button>
            <Button
              variant="contained"
              startIcon={<TrendingUpIcon />}
              onClick={() => setConvertOpen(true)}
              aria-label={t('leads.convert.submit')}
            >
              {t('leads.convert.submit')}
            </Button>
          </Stack>
        }
      />
      {editMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractApiErrorMessage(editMutation.error, t('leads.edit.error'))}
        </Alert>
      ) : null}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Field label="ФИО / Имя" value={`${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim() || '—'} />
              <Field label="Email" value={lead.email ?? '—'} />
              <Field label="Телефон" value={lead.phone ?? '—'} />
            </Stack>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Field label="Компания" value={lead.companyName ?? '—'} />
              <Field label="Номер лицензии" value={lead.licenseNumber ?? '—'} />
              <Field label="Имя сайта" value={lead.siteName ?? '—'} />
              <Field label="IP адрес" value={lead.ipAddress ?? '—'} />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <LeadStatusChip status={lead.status} />
              <Typography variant="body2" color="text.secondary">
                Источник: {formatLeadSource(lead.source, t)}
              </Typography>
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Заметки
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {lead.notes ?? '—'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Создано: {formatDate(lead.createdAt)} · Изменено: {formatDate(lead.updatedAt)}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* V12 U-35-C: блок «Платежи» лида — загрузка чека прямо в карточке (постановка R67). */}
      <PaymentBlock entityType="LEAD" entityId={lead.id} />

      {convertOpen ? (
        <LeadConvertDialog open={convertOpen} lead={lead} onClose={() => setConvertOpen(false)} onConverted={handleConverted} />
      ) : null}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('leads.edit.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Заголовок"
              required
              fullWidth
              value={editValues?.title ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, title: e.target.value } : v))}
              inputProps={{ 'aria-label': 'Заголовок' }}
            />
            <TextField
              label="Имя"
              fullWidth
              value={editValues?.firstName ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, firstName: e.target.value } : v))}
              inputProps={{ 'aria-label': 'Имя' }}
            />
            <TextField
              label="Фамилия"
              fullWidth
              value={editValues?.lastName ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, lastName: e.target.value } : v))}
              inputProps={{ 'aria-label': 'Фамилия' }}
            />
            <TextField
              label="Email"
              fullWidth
              value={editValues?.email ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, email: e.target.value } : v))}
              inputProps={{ 'aria-label': 'Email' }}
            />
            <TextField
              label="Телефон"
              fullWidth
              value={editValues?.phone ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, phone: e.target.value } : v))}
              inputProps={{ 'aria-label': 'Телефон' }}
            />
            <TextField
              label="Компания"
              fullWidth
              value={editValues?.companyName ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, companyName: e.target.value } : v))}
              inputProps={{ 'aria-label': 'Компания' }}
            />
            <TextField
              label="Номер лицензии"
              fullWidth
              value={editValues?.licenseNumber ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, licenseNumber: e.target.value } : v))}
              inputProps={{ 'aria-label': 'Номер лицензии' }}
            />
            <TextField
              label="Имя сайта"
              fullWidth
              value={editValues?.siteName ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, siteName: e.target.value } : v))}
              inputProps={{ 'aria-label': 'Имя сайта' }}
            />
            <TextField
              label="IP адрес"
              fullWidth
              value={editValues?.ipAddress ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, ipAddress: e.target.value } : v))}
              inputProps={{ 'aria-label': 'IP адрес' }}
            />
            <TextField
              label="Статус"
              select
              fullWidth
              value={editValues?.status ?? 'NEW'}
              onChange={(e) => setEditValues((v) => (v ? { ...v, status: e.target.value as LeadStatus } : v))}
              inputProps={{ 'aria-label': 'Статус' }}
            >
              {LEAD_STATUS_ORDER.map((status) => (
                <MenuItem key={status} value={status}>
                  {formatLeadStatus(status, t)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Заметки"
              fullWidth
              multiline
              minRows={3}
              value={editValues?.notes ?? ''}
              onChange={(e) => setEditValues((v) => (v ? { ...v, notes: e.target.value } : v))}
              inputProps={{ 'aria-label': 'Заметки' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => editMutation.mutate()}
            disabled={editMutation.isPending || !editValues || !editValues.title}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
